import { styled } from "@mui/material";
import { DataGridPro, DataGridProProps, GridApiPro } from "@mui/x-data-grid-pro";
import type * as Types from "@sinch/types/sinch.types";
import clsx from "clsx";
import * as React from "react";
import { MutableRefObject, ReactNode, useCallback } from "react";
import { DetailPanelContent } from "@sinch/components/application/RateComponentsMarginTable/DetailPanelContent";
import { useComponentRatesColumns } from "@sinch/components/application/RateComponentsMarginTable/hooks";
import { useBuildRows } from "@sinch/components/application/RateComponentsMarginTable/UseBuildRows";
import { TOTAL_ROW_ID } from "@sinch/components/application/RatesCompositionTable/EntityOverviewTable/hooks/useTreeQueries";

export type RateComponentsDetail = {
  amount?: number;
  component: string;
  parentRateAmount?: number | null;
  ratelistNodeId?: string;
  total: number;
  unit?: number | null;
  unitName?: Types.UnitName | null;
  unroundedUnit?: number | null;
  rateUnitName?: string;
};
export type CalculatedRateItems = {
  total: number;
  components: RateComponentsDetail[];
};

export type ComponentRow = {
  path?: string[];
  label?: ReactNode;
  id: string;
  component: string;
  price: number;
  wage: number;
  total: number;
  priceDetails: CalculatedRateItems[];
  wageDetails: CalculatedRateItems[];
  priceOverview?: CalculatedRateItems["components"][0][];
  wageOverview?: CalculatedRateItems["components"][0][];
};
export const RateComponentsMargin = ({
  priceDetails,
  wageDetails,
  width,
  apiRef,
  nameRowFlex,
  hideDetails,
  disableHeaders,
  totalRow,
}: {
  hideDetails?: boolean;
  nameRowFlex?: boolean;
  width?: number | string;
  priceDetails: CalculatedRateItems[];
  wageDetails: CalculatedRateItems[];
  apiRef: MutableRefObject<GridApiPro>;
  disableHeaders?: boolean;
  totalRow?: boolean;
}) => {
  const buildRows = useBuildRows(totalRow);

  const rows = buildRows(priceDetails, wageDetails);

  const groupingColDef: DataGridProProps["groupingColDef"] = {
    headerName: "",
    flex: nameRowFlex ? 1 : undefined,
    renderCell: ({ row }) => row.label,
  };

  const getDetailPanelContent = useCallback(
    ({ row }: { row: ComponentRow; id: string | number }) => <DetailPanelContent row={row} />,
    []
  );

  const getRowClassName = useCallback(
    ({ id }: { id: string | number }) => {
      const childrenCount = apiRef.current?.getRowGroupChildren({ groupId: id })?.length ?? 0;
      const isDetailExpanded = apiRef.current?.getExpandedDetailPanels?.()?.has(id) ?? false;
      return clsx(
        childrenCount > 0 && "TableLikeDataGrid-foldableRow",
        isDetailExpanded && "TableLikeDataGrid-detailExpandedRow",
        id === TOTAL_ROW_ID ? "TableLikeDataGrid-totalRow" : "TableLikeDataGrid-row"
      );
    },
    [apiRef]
  );

  const columns = useComponentRatesColumns();

  return (
    <CalculationDataGrid<ComponentRow>
      apiRef={apiRef}
      autoHeight
      autosizeOnMount={nameRowFlex}
      autosizeOptions={
        nameRowFlex
          ? {
              columns: ["price", "wage", "total", "marginPercentage"],
              includeHeaders: true,
              includeOutliers: true,
              expand: true,
            }
          : undefined
      }
      classes={{
        columnHeader: "TableLikeDataGrid-columnHeader",
        columnHeaderTitle: "TableLikeDataGrid-columnHeaderTitle",
      }}
      columns={columns}
      density="compact"
      disableColumnFilter
      disableColumnMenu
      disableColumnReorder
      disableColumnSelector
      disableColumnSorting
      disableRowSelectionOnClick
      disableVirtualization
      getDetailPanelContent={!hideDetails ? getDetailPanelContent : undefined}
      getDetailPanelHeight={() => "auto"}
      getRowClassName={getRowClassName}
      getTreeDataPath={(row: ComponentRow) => row.path ?? []}
      groupingColDef={groupingColDef}
      hideFooter
      rows={rows}
      slots={
        disableHeaders
          ? {
              columnHeaders: () => null,
            }
          : undefined
      }
      sx={{ width: width, overflowX: "hidden" }}
      treeData
    />
  );
};
const CalculationDataGrid = styled(DataGridPro)<DataGridProProps>(({ theme }) => ({
  "& .MuiDataGrid-row--detailPanelExpanded": {
    backgroundColor: theme.palette.custom.deepSpace["200"],
    "&.TableLikeDataGrid-detailExpandedRow": {
      backgroundColor: "transparent",
    },
  },
  "& .MuiDataGrid-detailPanel": {
    backgroundColor: theme.palette.grey["100"],
    overflow: "visible",
  },

  "& .MuiDataGrid-columnHeaders .MuiDataGrid-filler": {
    backgroundColor: theme.palette.grey["200"],
  },
  "& .TableLikeDataGrid-columnHeader": {
    backgroundColor: theme.palette.grey["200"],
    "& .TableLikeDataGrid-columnHeaderTitle": {
      fontWeight: theme.typography.fontWeightBold,
      color: theme.palette.text.primary,
    },
  },

  "& .MuiDataGrid-detailPanel>div": {
    marginLeft: 0,
  },
  "& .TableLikeDataGrid-totalRow": { backgroundColor: "#FFF5E8" },
  "& .TableLikeDataGrid-row.TableLikeDataGrid-foldableRow": { cursor: "pointer" },
  "& .MuiDataGrid-cell": {
    borderBottom: "none",
  },
})) as typeof DataGridPro;
